import type { UsersWithRelations } from "@ch/generated";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector";
import { updateAppKemState } from "@ch/redux-store/kem/actions";
import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import getKemUserSession from "../utils/getKemUserSession";
import { handleError } from "../utils/handleError";
import { NotifyError } from "../utils/helpers";
import useLoading from "./useLoading";

interface KemUserSessionProps {
  onSuccess?: (data: UsersWithRelations) => void;
  onFailure?: () => void;
}

const useKemUserSession = () => {
  const { loading, startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();

  const currentUser = useAppSelector((state) => state.user.currentUser);
  const kemUserSession = useAppSelector((state) => state.kem.kemUserSession);
  const kemUserSessionRef = useRef(kemUserSession);
  kemUserSessionRef.current = kemUserSession;

  const userId = currentUser?.id;

  const initiateKemUserSession = useCallback(
    async ({ onSuccess, onFailure }: KemUserSessionProps = {}) => {
      startLoading();
      try {
        if (!userId) {
          NotifyError("User not found");
          return;
        }

        const data = await getKemUserSession({ userId });

        if (!data) {
          if (kemUserSessionRef.current) {
            // eslint-disable-next-line no-console
            console.warn(
              "[useKemUserSession] Failed to refresh KEM user session; keeping existing session",
            );
            onFailure?.();
            return;
          }

          NotifyError("Kem user session not found");
          onFailure?.();
          return;
        }

        dispatch(
          updateAppKemState({
            kemUserSession: data,
          }),
        );

        onSuccess?.(data);
      } catch (error) {
        handleError(error);
        onFailure?.();
      } finally {
        stopLoading();
      }
    },
    [dispatch, startLoading, stopLoading, userId],
  );

  return {
    loading,
    initiateKemUserSession,
  };
};

export default useKemUserSession;
